// @givmo/board — the Board section of a class console's composed Settings page (CBG-MOD1; D-CBG-9,
// 2026-09-28: "this is something htat should just come up under a Board Settings page"). Board is the
// module-settings seam's first declarer (Org Machinery OM-17, seam B: a module-owned panel over the
// module's own transport).
//
// One panel: "Calendar", Board's calendar settings in one place (CBG-UX14; the simple card of
// D-CBG-56, 2026-11-01): what sends the reader's meetings and the one act on it, an Owner's Move
// for an officer who left, then what invitations allow. It replaced the calendar's two cards
// ("Calendars" and "Calendar invitations", CBG-CAL1 slice 1d). "Record retention" and "Legal holds"
// left Board's Settings with their dialogs (D-CBG-53, 2026-11-01 14:57 PST): the backend's
// retention and legal-hold machinery and routes stay, a record still says where a hold or a longer
// minimum affects it, and an organization's request goes to Givmo support. (A third card, "How
// directors sign in", retired with the option itself, D-CBG-20: every board has one rule.) What the
// seam adds is the gate and the frame:
//
//   * THE GATE IS THE WRITE STRING. The Settings page mounts a panel only for a holder of the ONE
//     string its lowest write requires, never a disabled control (the seam's law,
//     `settings-declarations.ts`): `board_meeting:write`, the string scheduling a meeting and
//     connecting one's own calendar ride, so an officer who schedules meetings but isn't an Owner
//     finds where their meetings go out from in the same place. Inside, every Owner act (the board
//     account, another officer's calendar, Change, Apply) shows only for `board_calendar:manage`.
//     A person holding only `board:read` sees no Board panel (the orchestrator's ruling, CBG-MOD1).
//   * THE FRAME IS THE PAGE'S. The page draws the "Board settings" heading; the panel draws its one
//     card and no heading of its own, reports its dialog's unsaved input through `registerDirty`, and
//     shows a refusal inline (a dialog's backend sentence, or the card's own read error).
//
// A panel mounts outside the module's page gate, so it brings the officer host its card reads from:
// the organization and the settled permission set the page hands every panel, and (for the calendar's
// words) the organization's name and the person's backend id off the console's config.
//
// ⚠ THE CARD IS DARK UNTIL SERVED. Its settings read and Board's connections read are the connected
// channel's, so while `BOARD_CALENDAR_INVITATIONS_ENABLED` is off the card renders nothing (and also
// where Google isn't offered and nothing sends). The composer's slot takes no space when a panel
// renders nothing, and with every slot empty the composer hides Board's whole section, heading
// included, so Settings never shows "Board settings" over nothing (CBG-UX14's review, F1).

import type { ReactNode } from 'react';
import type { ModuleSettings, SettingsPanelProps } from '@givmo/org-console';
import { BoardOfficerProvider } from './board-host';
import { BOARD_SLUG } from './board-routes';
import {
  BoardCalendarCard,
  CALENDAR_SCHEDULE_PERMISSION,
} from './features/calendar/board-calendar-card';

function PanelHost({
  orgRef,
  permissions,
  config,
  children,
}: Pick<SettingsPanelProps, 'orgRef' | 'permissions'> &
  Partial<Pick<SettingsPanelProps, 'config'>> & { children: ReactNode }) {
  return (
    <BoardOfficerProvider
      host={{
        orgRef,
        isReady: true,
        isPending: false,
        isError: false,
        error: null,
        permissions,
        userId: config?.selfUserId ?? null,
        orgName: config?.vocab.orgName ?? null,
      }}
    >
      {children}
    </BoardOfficerProvider>
  );
}

/** "Calendar": what sends the reader's meetings, then what invitations allow (D-CBG-56). */
export function BoardCalendarSettingsPanel({
  orgRef,
  permissions,
  config,
  registerDirty,
}: SettingsPanelProps) {
  return (
    <PanelHost orgRef={orgRef} permissions={permissions} config={config}>
      <BoardCalendarCard onDirtyChange={registerDirty} />
    </PanelHost>
  );
}

/** Board's declared settings, registered beside its nav in a host's console declarations. */
export const BOARD_SETTINGS: ModuleSettings = {
  slug: BOARD_SLUG,
  panels: [
    {
      id: 'calendar',
      order: 12,
      requires: CALENDAR_SCHEDULE_PERMISSION,
      component: BoardCalendarSettingsPanel,
    },
  ],
};
